import React, { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';

import PassengerForm from 'components/PassengerForm';
import NonRefundableWarning from 'components/NonRefundableWarning';
import Footer from 'components/Safra/Footer';
import { useTranslation } from 'contexts/Safra/TranslationContext';
import { useAuth } from 'contexts/Safra/AuthProvider';
import { useApp } from 'contexts/AppContext';
import { sendPassengers } from '_services/airfare.service';
import { trackFlightGuests } from 'utils/analytics';
import { usaPontos } from 'utils';

import { PageContainer, PageTitle, PageSubtitle, SubmitButton } from './styles';
import { checkoutAirfareAction } from 'contexts/Safra/actions';

const buildInitialPassengers = (adults, children, baby) => {
  const passengers = [];
  for (let i = 0; i < adults; i++) {
    passengers.push({ firstName: '', lastName: '', birthDate: '', gender: '', documentType: 'CPF', document: '', passportNumber: '', passportValidity: '', passportNationality: '', passportIssuingCountry: '', type: 'ADT', isSelf: false });
  }
  for (let i = 0; i < children; i++) {
    passengers.push({ firstName: '', lastName: '', birthDate: '', gender: '', documentType: 'CPF', document: '', passportNumber: '', passportValidity: '', passportNationality: '', passportIssuingCountry: '', type: 'CHD', isSelf: false });
  }
  for (let i = 0; i < baby; i++) {
    passengers.push({ firstName: '', lastName: '', birthDate: '', gender: '', documentType: 'CPF', document: '', passportNumber: '', passportValidity: '', passportNationality: '', passportIssuingCountry: '', type: 'INF', isSelf: false });
  }
  return passengers;
};

const getValidationSchema = (t) => Yup.object().shape({
  passengers: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string()
        .required(t('safra.passenger_data.validation.first_name_required'))
        .min(2, t('safra.passenger_data.validation.too_short', { min: 2 }))
        .max(29, t('safra.passenger_data.validation.too_long', { max: 29 }))
        .matches(/^[a-zA-ZÀ-ÿ\s]+$/, t('safra.passenger_data.validation.invalid_name')),
      lastName: Yup.string()
        .required(t('safra.passenger_data.validation.last_name_required'))
        .min(2, t('safra.passenger_data.validation.too_short', { min: 2 }))
        .max(29, t('safra.passenger_data.validation.too_long', { max: 29 }))
        .matches(/^[a-zA-ZÀ-ÿ\s]+$/, t('safra.passenger_data.validation.invalid_last_name')),
      birthDate: Yup.string()
        .required(t('safra.passenger_data.validation.birth_date_required'))
        .matches(/^\d{2}\/\d{2}\/\d{4}$/, t('safra.passenger_data.validation.invalid_date'))
        .test('valid-date', t('safra.passenger_data.validation.invalid_date'), (value) => {
          if (!value || !/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;
          const [day, month, year] = value.split('/').map(Number);
          const date = new Date(year, month - 1, day);
          return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
        }),
      gender: Yup.string()
        .required(t('safra.passenger_data.validation.gender_required')),
      documentType: Yup.string()
        .required(t('safra.passenger_data.validation.document_type_required')),
      document: Yup.string()
        .when('documentType', {
          is: 'CPF',
          then: (schema) => schema
            .required(t('safra.passenger_data.validation.cpf_required'))
            .test('cpf-valid', t('safra.passenger_data.validation.invalid_cpf'), (value) => cpfValidator.isValid(value || '')),
          otherwise: (schema) => schema.notRequired(),
        }),
      passportNumber: Yup.string()
        .when('documentType', {
          is: 'Passaporte',
          then: (schema) => schema.required(t('safra.passenger_data.validation.passport_required')).max(15, t('safra.passenger_data.validation.too_long', { max: 15 })),
          otherwise: (schema) => schema.notRequired(),
        }),
      passportValidity: Yup.string()
        .when('documentType', {
          is: 'Passaporte',
          then: (schema) => schema
            .required(t('safra.passenger_data.validation.validity_required'))
            .matches(/^\d{2}\/\d{2}\/\d{4}$/, t('safra.passenger_data.validation.invalid_date'))
            .test('valid-date', t('safra.passenger_data.validation.invalid_date'), (value) => {
              if (!value || !/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;
              const [day, month, year] = value.split('/').map(Number);
              const date = new Date(year, month - 1, day);
              return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
            }),
          otherwise: (schema) => schema.notRequired(),
        }),
      passportNationality: Yup.string()
        .when('documentType', {
          is: 'Passaporte',
          then: (schema) => schema.required(t('safra.passenger_data.validation.nationality_required')).max(100, t('safra.passenger_data.validation.too_long', { max: 100 })),
          otherwise: (schema) => schema.notRequired(),
        }),
      passportIssuingCountry: Yup.string()
        .when('documentType', {
          is: 'Passaporte',
          then: (schema) => schema.required(t('safra.passenger_data.validation.country_required')).max(100, t('safra.passenger_data.validation.too_long', { max: 100 })),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
  ),
});

export default function PassengerDataPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { flight, searchParams } = location.state || {};

  const { userData, customerId } = useAuth();
  const { airfareState } = useApp();
  const [submitError, setSubmitError] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);


  const customerData = {
    firstName: userData?.nome || '',
    lastName: userData?.sobrenome || '',
    document: userData?.cpf || '',
  };

  const initialPassengers = useMemo(() => {
    if (!searchParams) return [];
    return buildInitialPassengers(
      parseInt(searchParams.adults) || 1,
      parseInt(searchParams.children) || 0,
      parseInt(searchParams.baby) || 0
    );
  }, [searchParams]);

  const getPassengerTypeFromBirthDate = (birthDateStr) => {
    if (!birthDateStr || !/^\d{2}\/\d{2}\/\d{4}$/.test(birthDateStr)) return 'adult';
    const [day, month, year] = birthDateStr.split('/').map(Number);
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    if (age <= 2) return 'baby';
    if (age <= 12) return 'children';
    return 'adult';
  };

  const mapPassengerTitle = (gender, type) => {
    const isChild = type === 'children' || type === 'baby';
    if (gender === 'M') return isChild ? 'MSTR' : 'MR';
    return isChild ? 'MISS' : 'MRS';
  };

  const formatDateToApi = (dateStr) => {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitError(null);

    const typeCounts = { adult: 0, children: 0, baby: 0 };
    values.passengers.forEach((p) => {
      const type = getPassengerTypeFromBirthDate(p.birthDate);
      typeCounts[type]++;
    });

    const passengers = values.passengers.map((p) => {
      const isCpf = p.documentType === 'CPF';
      const base = {
        name: p.firstName,
        last_name: p.lastName,
        gender: p.gender === 'M' ? 'male' : 'female',
        birthdate: formatDateToApi(p.birthDate),
        id_type: isCpf ? 'ID' : 'PP',
      };

      if (isCpf) {
        base.cpf = p.document.replaceAll('.', '').replace('-', '');
        base.nationality = 'BR';
      } else {
        base.passport_number = p.passportNumber;
        base.passport_date = formatDateToApi(p.passportValidity);
        base.passport_country = p.passportIssuingCountry;
        base.nationality = p.passportNationality;
      }

      return base;
    });

    const response = await sendPassengers({
      chooseId: airfareState.chooseId,
      passengers,
    });

    if (response.status) {
      const travellers = values.passengers.map((p) => {
        const [day, month, year] = p.birthDate.split('/');
        const identification = p.documentType === 'CPF'
          ? p.document.replaceAll('.', '').replace('-', '')
          : p.passportNumber;

        return {
          FirstName: p.firstName,
          MiddleName: '',
          LastName: p.lastName,
          BirthDate: `${year}-${month}-${day}`,
          Gender: p.gender === 'M' ? 'Male' : 'Female',
          Type: getPassengerTypeFromBirthDate(p.birthDate),
          Identification: identification,
          IdType: p.documentType === 'CPF' ? 'CPF' : 'PASSPORT',
          Nationality: p.documentType === 'Passaporte' ? p.passportNationality : 'BR',
          ...(p.documentType === 'Passaporte' && {
            PassportValidity: p.passportValidity,
            PassportIssuingCountry: p.passportIssuingCountry,
          }),
        };
      });

      localStorage.setItem('selected_flight', JSON.stringify(flight));
      localStorage.setItem('selected_travellers', JSON.stringify(travellers));

      trackFlightGuests({ status: 'Sucesso' });
      console.log(airfareState, flight)

      const checkout = await checkoutAirfareAction({
            customerId,
            flight: flight,
            travellers: travellers,
            offersAirfare: usaPontos() ? 'points' : 'cash',
            chooseId: airfareState.chooseId
        });

      if (checkout?.data && checkout?.hash) {
          window.parent.postMessage({
              'message': 'start-checkout-aereo',
              payload: checkout.data,
              hash: checkout.hash
          }, '*');
          return;
      }
      
    } else {
      setSubmitError(t('safra.passenger_data.messages.error_sending'));
      trackFlightGuests({ status: 'Erro', error_field: 'api_error' });
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: { passengers: initialPassengers },
    validationSchema: useMemo(() => getValidationSchema(t), [t]),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (formik.submitCount > 0 && !formik.isSubmitting && !formik.isValid) {
      const passengersErrors = formik.errors.passengers;
      if (passengersErrors && Array.isArray(passengersErrors)) {
        const fieldMapping = {
          firstName: 'nome',
          lastName: 'sobrenome',
          birthDate: 'data_nascimento',
          gender: 'sexo',
          documentType: 'tipo_documento',
          document: 'documento',
          passportNumber: 'documento',
          passportValidity: 'validade_passaporte',
          passportNationality: 'nacionalidade_passaporte',
          passportIssuingCountry: 'pais_emissao_passaporte'
        };

        const allErrors = new Set();
        passengersErrors.forEach((passengerError) => {
          if (passengerError && typeof passengerError === 'object') {
            Object.keys(passengerError).forEach((fieldName) => {
              const mappedName = fieldMapping[fieldName] || fieldName;
              allErrors.add(mappedName);
            });
          }
        });

        if (allErrors.size > 0) {
          const error_field = Array.from(allErrors).join(', ');
          trackFlightGuests({ status: 'Erro', error_field });
        }
      } else if (typeof passengersErrors === 'string') {
        trackFlightGuests({ status: 'Erro', error_field: 'passengers_array' });
      }
    }
  }, [formik.submitCount, formik.isSubmitting, formik.isValid, formik.errors.passengers]);

  if (!flight || !searchParams) {
    return <Navigate to="/passage-search-result" />;
  }

  return (
    <>
      <PageContainer>
        <PageTitle style={{ marginTop: '20px' }}>{t('safra.passenger_data.title')}</PageTitle>
        <PageSubtitle>
          {t('safra.passenger_data.subtitle')}
        </PageSubtitle>

        {submitError && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: '#c00',
            fontSize: '14px',
            fontFamily: 'var(--font-family-base)'
          }}>
            {submitError}
          </div>
        )}

        <form onSubmit={formik.handleSubmit}>
          {formik.values.passengers.map((_, index) => (
            <PassengerForm
              key={index}
              index={index}
              showSelfCheckbox={index === 0}
              formik={formik}
              customerData={customerData}
            />
          ))}
          <SubmitButton type="submit" disabled={formik.isSubmitting || !formik.isValid || isRedirecting}>
            {formik.isSubmitting || isRedirecting ? t('safra.passenger_data.cta_sending') : t('safra.passenger_data.cta_submit')}
          </SubmitButton>
        </form>
      </PageContainer>
      <Footer />
    </>
  );
}
